import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconDownload,
  IconKey,
  IconShield,
} from '@tabler/icons-react';
import { saveAs } from 'file-saver';
import {
  exportFlow,
  getMethodDetail,
  getServiceDetail,
  listFlowsForService,
  listIntegrations,
  listMethodsForIntegration,
  listServices,
} from '../../api_scripts/connectExporterClient';
import { scrubBundle } from '../../utils/connectExportScrubber';
import { ActivityLog, ProgressBar, useActivityLog } from './activityLog';

const DEFAULT_BASE_URL = 'https://prisma.us.webexconnect.io';

function tenantSlugFromBaseUrl(baseUrl) {
  try {
    const host = new URL(baseUrl).host;
    return host.split('.')[0] || 'tenant';
  } catch {
    return 'tenant';
  }
}

export default function BackupPanel() {
  const [baseUrl, setBaseUrl] = useState(DEFAULT_BASE_URL);
  const [token, setToken] = useState('');
  const [exportNodes, setExportNodes] = useState(true);
  const [exportServices, setExportServices] = useState(true);
  const [includeSecrets, setIncludeSecrets] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState(null);
  const { log, append: appendLog, reset: resetLog } = useActivityLog();

  const canStart = useMemo(
    () =>
      !running &&
      baseUrl.trim().length > 0 &&
      token.trim().length > 0 &&
      (exportNodes || exportServices),
    [running, baseUrl, token, exportNodes, exportServices],
  );

  async function runExport() {
    setRunning(true);
    setError(null);
    setBundle(null);
    resetLog();
    setProgress({ done: 0, total: 0 });

    const trimmedBase = baseUrl.replace(/\/+$/, '');
    const trimmedToken = token.trim();
    const result = {
      exportedAt: new Date().toISOString(),
      baseUrl: trimmedBase,
      scope: { nodes: exportNodes, services: exportServices, includeSecrets },
      integrations: null,
      services: null,
    };

    try {
      let nodePlan = [];
      let servicePlan = [];
      if (exportNodes) {
        appendLog('info', 'Listing custom integrations…');
        const integrations = await listIntegrations(trimmedBase, trimmedToken);
        appendLog('success', `Found ${integrations.length} integrations.`);
        for (const integ of integrations) {
          appendLog(
            'info',
            `Listing methods on "${integ.name}" (asset ${integ.asset_id})…`,
          );
          const methods = await listMethodsForIntegration(
            trimmedBase,
            trimmedToken,
            integ.asset_id,
          );
          appendLog('success', `  ${methods.length} methods on "${integ.name}".`);
          nodePlan.push({ integ, methods });
        }
      }
      if (exportServices) {
        appendLog('info', 'Listing services…');
        const services = await listServices(trimmedBase, trimmedToken);
        appendLog('success', `Found ${services.length} services.`);
        for (const svc of services) {
          appendLog('info', `Listing flows on service "${svc.name}" (${svc.id})…`);
          const flows = await listFlowsForService(trimmedBase, trimmedToken, svc.id);
          appendLog('success', `  ${flows.length} flows on "${svc.name}".`);
          servicePlan.push({ svc, flows });
        }
      }

      const methodCount = nodePlan.reduce((n, p) => n + p.methods.length, 0);
      const serviceDetailCount = servicePlan.length;
      const flowCount = servicePlan.reduce((n, p) => n + p.flows.length, 0);
      setProgress({ done: 0, total: methodCount + serviceDetailCount + flowCount });

      if (exportNodes) {
        result.integrations = [];
        for (const { integ, methods } of nodePlan) {
          const integEntry = { ...integ, methods: [] };
          for (const m of methods) {
            appendLog(
              'info',
              `Fetching nodemethod ${integ.asset_id}/${m.id} (${m.method_name})…`,
            );
            const detail = await getMethodDetail(
              trimmedBase,
              trimmedToken,
              integ.asset_id,
              m.id,
            );
            integEntry.methods.push({ summary: m, detail });
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          }
          result.integrations.push(integEntry);
        }
      }

      if (exportServices) {
        result.services = [];
        for (const { svc, flows } of servicePlan) {
          appendLog('info', `Fetching service detail ${svc.id}…`);
          const detail = await getServiceDetail(trimmedBase, trimmedToken, svc.id);
          setProgress((p) => ({ ...p, done: p.done + 1 }));
          const flowEntries = [];
          for (const f of flows) {
            appendLog('info', `Exporting flow ${svc.id}/${f.id} (${f.name})…`);
            try {
              const hex = await exportFlow(trimmedBase, trimmedToken, {
                serviceId: svc.id,
                flowId: f.id,
                paletteName: f.palette_name,
                paletteType: f.type || 'workflow',
              });
              flowEntries.push({ summary: f, exportHex: hex });
            } catch (e) {
              appendLog('warning', `  flow ${f.id} export failed: ${e.message}`);
              flowEntries.push({ summary: f, exportHex: null, error: e.message });
            }
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          }
          result.services.push({ summary: svc, detail, flows: flowEntries });
        }
      }

      const finalBundle = includeSecrets ? result : scrubBundle(result);
      setBundle(finalBundle);
      appendLog(
        'success',
        includeSecrets
          ? 'Export complete — secrets INCLUDED (keep this file private).'
          : 'Export complete — secrets scrubbed.',
      );
    } catch (e) {
      console.error(e);
      setError(e.message || String(e));
      appendLog('error', e.message || String(e));
    } finally {
      setRunning(false);
    }
  }

  function downloadBundle() {
    if (!bundle) return;
    const slug = tenantSlugFromBaseUrl(bundle.baseUrl);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const name = `connect-export-${slug}-${stamp}${
      bundle.scope.includeSecrets ? '-WITH-SECRETS' : ''
    }.json`;
    const blob = new Blob([JSON.stringify(bundle, null, 2)], {
      type: 'application/json',
    });
    saveAs(blob, name);
  }

  return (
    <Stack>
      <Text c="dimmed">
        Pull <strong>Custom Integrations</strong> (node methods — URL
        templates, headers, OAuth config, event mappings) and{' '}
        <strong>Services</strong> (metadata + per-flow export blob) from a
        tenant and download as a JSON bundle. Useful as a backup or for
        off-line review.
      </Text>

      <Paper withBorder p="md">
        <Stack gap="sm">
          <TextInput
            label="Tenant base URL"
            description="e.g. https://prisma.us.webexconnect.io"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.currentTarget.value)}
            leftSection={<IconKey size={14} />}
            disabled={running}
          />
          <PasswordInput
            label="accesstoken (from Connect SPA)"
            description="DevTools → Network → any connectapi/v1 request → Request Headers → accesstoken"
            value={token}
            onChange={(e) => setToken(e.currentTarget.value)}
            disabled={running}
          />
          <Group>
            <Checkbox
              label="Custom Nodes (integrations + methods)"
              checked={exportNodes}
              onChange={(e) => setExportNodes(e.currentTarget.checked)}
              disabled={running}
            />
            <Checkbox
              label="Services (metadata + flows)"
              checked={exportServices}
              onChange={(e) => setExportServices(e.currentTarget.checked)}
              disabled={running}
            />
          </Group>
          <Divider my="xs" />
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={2}>
              <Group gap="xs">
                <IconShield size={16} />
                <Text size="sm" fw={600}>
                  Include secrets in download
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                Scrubbed by default. Enabling writes OAuth client_secret, access
                tokens, service securedKey/jwtSecretKey in plaintext — treat the
                output as a credential bundle.
              </Text>
            </Stack>
            <Switch
              checked={includeSecrets}
              onChange={(e) => setIncludeSecrets(e.currentTarget.checked)}
              disabled={running}
              color="red"
              size="md"
            />
          </Group>
          {includeSecrets && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              The download will contain live credentials in plaintext. Do not
              commit, share, or leave in a Downloads folder — handle as you
              would a secrets vault export.
            </Alert>
          )}
          <Group>
            <Button
              onClick={runExport}
              disabled={!canStart}
              loading={running}
              color="blue"
            >
              Start export
            </Button>
            {bundle && (
              <Button
                leftSection={<IconDownload size={16} />}
                onClick={downloadBundle}
                variant="light"
                color="blue"
              >
                Download bundle
              </Button>
            )}
          </Group>
        </Stack>
      </Paper>

      <ProgressBar
        done={progress.done}
        total={progress.total}
        running={running}
      />

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {error}
        </Alert>
      )}

      <ActivityLog log={log} />

      <Box>
        <Text size="xs" c="dimmed">
          Flows are exported as the opaque hex blob Connect&apos;s own Export
          endpoint returns — stored as a hex string on each flow entry. Format
          is not JSON; the Transfer tab hex-decodes to bytes before uploading
          to a target tenant.
        </Text>
      </Box>
    </Stack>
  );
}
